/**
 * Live job counts per restaurant, sourced from Phenom's syndication feed.
 * The feed returns every active US posting in one call; we aggregate by
 * `glcompany` to produce { "Restaurant Name": count }. Cached at 24 hours
 * via Next.js `unstable_cache` (PDF spec'd integration cadence).
 *
 * The raw XML is ~3MB, over the 2MB ceiling on Next.js' fetch data cache. We
 * cache the parsed map (a few dozen entries) so the cache layer never sees
 * the XML.
 *
 * On HTTP/network failure we throw rather than returning an empty map so
 * `unstable_cache` does not freeze a failure for the full revalidate window.
 * The throw propagates to `augmentSectionsWithOpenRoleCounts`, which catches
 * and falls back to manual openings counts for that single render. The next
 * render retries Phenom.
 */

import { unstable_cache } from 'next/cache'

const PHENOM_FEED_URL =
  'https://jobboards.phenommarket.com/feeds/dtfdtfus-en-us-feed-generic-feedforcorporatesite'

export const PHENOM_JOB_COUNTS_TAG = 'phenom-job-counts'

const REVALIDATE_SECONDS = 86_400

// CDATA values can include leading/trailing whitespace per Phenom spec; trim defensively.
const GLCOMPANY_PATTERN = /<glcompany>(?:<!\[CDATA\[)?([\s\S]*?)(?:\]\]>)?<\/glcompany>/g

export type PhenomJobCounts = Record<string, number>

export const parseGlcompanyValues = (xml: string): string[] => {
  const values: string[] = []
  const pattern = new RegExp(GLCOMPANY_PATTERN.source, GLCOMPANY_PATTERN.flags)
  let match: RegExpExecArray | null
  while ((match = pattern.exec(xml)) !== null) {
    const value = match[1].trim()
    if (value) values.push(value)
  }
  return values
}

export const aggregateJobCounts = (values: string[]): PhenomJobCounts =>
  values.reduce<PhenomJobCounts>((acc, key) => {
    acc[key] = (acc[key] ?? 0) + 1
    return acc
  }, {})

const fetchAndAggregate = async (): Promise<PhenomJobCounts> => {
  const response = await fetch(PHENOM_FEED_URL, { cache: 'no-store' })

  if (!response.ok) {
    throw new Error(`Phenom feed responded ${response.status}`)
  }

  const xml = await response.text()
  return aggregateJobCounts(parseGlcompanyValues(xml))
}

export const getOpenRoleCounts = unstable_cache(
  fetchAndAggregate,
  ['phenom-job-counts'],
  { revalidate: REVALIDATE_SECONDS, tags: [PHENOM_JOB_COUNTS_TAG] },
)
